/**
 * Zoekopdrachten voor Realworks, klaargezet in MamiMap (MA-66).
 *
 * Bij een taxatie klik je op de kaart aan welke postcodes rond het doelpand je
 * wilt doorzoeken. MamiMap maakt daar zoekopdrachten van voor de Uitwisseling
 * van Realworks: per aaneengesloten reeks postcodes één opdracht, met filters die
 * uit de woning komen (m², soort, periode). De extensie vult er het zoekscherm
 * van Realworks mee in en stuurt de uitkomst, met foto's, terug als transacties.
 *
 * Realworks zoekt op een postcode "van ... t/m ...". Zo'n reeks bevat alles wat
 * er alfabetisch tussen ligt, ook postcodes die je niet aanklikte. Daarom maakt
 * `postcodeReeksen` alleen een reeks van postcodes die in de lijst van de buurt
 * direct op elkaar volgen; een overgeslagen postcode begint een nieuwe opdracht.
 */
import { z } from 'zod';

const geheel = z.number().int();
const postcode6 = z
  .string()
  .trim()
  .transform((waarde) => waarde.replace(/\s+/g, '').toUpperCase())
  .pipe(z.string().regex(/^[1-9]\d{3}[A-Z]{2}$/, 'Geen postcode.'));
const datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als jjjj-mm-dd.');

/** Eén postcode rond het doelpand, als punt op de kaart. */
export const postcodePuntSchema = z.object({
  postcode: z.string(),
  lat: z.number(),
  lng: z.number(),
  /** De straat, of de straten als de postcode er meer dan één raakt. */
  straat: z.string(),
  huisnrVan: geheel,
  huisnrTm: geheel,
  woningen: geheel,
  /** Hemelsbreed van het doelpand tot het dichtstbijzijnde adres met deze postcode. */
  afstandM: geheel,
});
export type PostcodePunt = z.infer<typeof postcodePuntSchema>;
export const postcodePuntenSchema = z.array(postcodePuntSchema);

export const postcodesQuerySchema = z.object({
  lat: z.coerce.number().min(50).max(54),
  lng: z.coerce.number().min(3).max(8),
  straalM: z.coerce.number().int().min(50).max(1000).default(500),
});
export type PostcodesQuery = z.infer<typeof postcodesQuerySchema>;

export const REALWORKS_SOORTEN = ['alles', 'appartement', 'woonhuis'] as const;
export const realworksSoortSchema = z.enum(REALWORKS_SOORTEN);
export type RealworksSoort = z.infer<typeof realworksSoortSchema>;

/** Wat de extensie in het zoekscherm zet naast de postcodes. Status is altijd Verkocht. */
export const realworksFiltersSchema = z.object({
  m2Van: geheel.min(0).max(2000).nullable().default(null),
  m2Tm: geheel.min(0).max(2000).nullable().default(null),
  soort: realworksSoortSchema.default('alles'),
  /** Ondertekening van de koopovereenkomst, van en tot en met. */
  datumVan: datum.nullable().default(null),
  datumTm: datum.nullable().default(null),
});
export type RealworksFilters = z.infer<typeof realworksFiltersSchema>;

export const REALWORKS_ZOEK_STATUSSEN = ['open', 'klaar', 'mislukt'] as const;
export const realworksZoekStatusSchema = z.enum(REALWORKS_ZOEK_STATUSSEN);

export const realworksUitkomstSchema = z.object({
  /** Rijen in de lijst van Realworks na het zoeken. */
  rijen: geheel.min(0),
  transacties: geheel.min(0),
  fotos: geheel.min(0),
  melding: z.string().max(300).nullable().default(null),
});
export type RealworksUitkomst = z.infer<typeof realworksUitkomstSchema>;

export const realworksZoekopdrachtSchema = z.object({
  id: z.uuid(),
  woningId: z.uuid().nullable(),
  naam: z.string(),
  postcodeVan: z.string(),
  postcodeTm: z.string(),
  /** Hoeveel woningen de reeks volgens de BAG heeft; om te zien hoe groot hij is. */
  woningen: geheel.nullable(),
  filters: realworksFiltersSchema,
  status: realworksZoekStatusSchema,
  aangemaaktOp: z.string(),
  uitgevoerdOp: z.string().nullable(),
  uitkomst: realworksUitkomstSchema.nullable(),
});
export type RealworksZoekopdracht = z.infer<typeof realworksZoekopdrachtSchema>;
export const realworksZoekopdrachtenSchema = z.array(realworksZoekopdrachtSchema);

export const realworksReeksSchema = z.object({
  postcodeVan: postcode6,
  postcodeTm: postcode6,
  woningen: geheel.min(0).nullable().default(null),
});
export type RealworksReeks = z.infer<typeof realworksReeksSchema>;

export const nieuweRealworksZoekopdrachtenSchema = z.object({
  woningId: z.uuid().nullable().default(null),
  reeksen: z.array(realworksReeksSchema).min(1).max(60),
  filters: realworksFiltersSchema,
});
export type NieuweRealworksZoekopdrachten = z.infer<typeof nieuweRealworksZoekopdrachtenSchema>;

export const realworksZoekQuerySchema = z.object({ woning: z.uuid().optional() });

/** De naam van een reeks zoals je hem in Realworks zou typen. */
export function reeksNaam(van: string, tm: string): string {
  return van === tm ? van : `${van} t/m ${tm}`;
}

/**
 * De gekozen postcodes als aaneengesloten reeksen.
 *
 * `alle` is de lijst postcodes die er in de buurt zijn (de punten op de kaart).
 * Twee gekozen postcodes horen in dezelfde reeks als er in `alle` niets tussen
 * ligt dat niet gekozen is; anders zou de reeks in Realworks een postcode
 * meenemen die je juist wegliet. Een reeks over een ander viercijferig deel heen
 * maken we nooit.
 */
export function postcodeReeksen(
  gekozen: Iterable<string>,
  alle: readonly { postcode: string; woningen?: number }[],
): RealworksReeks[] {
  const keuze = new Set(gekozen);
  const lijst = [...new Map(alle.map((punt) => [punt.postcode, punt])).values()].sort((a, b) =>
    a.postcode.localeCompare(b.postcode),
  );
  const reeksen: RealworksReeks[] = [];
  let huidige: RealworksReeks | null = null;
  for (const punt of lijst) {
    if (!keuze.has(punt.postcode)) {
      huidige = null;
      continue;
    }
    if (huidige && huidige.postcodeTm.slice(0, 4) === punt.postcode.slice(0, 4)) {
      huidige.postcodeTm = punt.postcode;
      huidige.woningen = (huidige.woningen ?? 0) + (punt.woningen ?? 0);
    } else {
      huidige = {
        postcodeVan: punt.postcode,
        postcodeTm: punt.postcode,
        woningen: punt.woningen ?? 0,
      };
      reeksen.push(huidige);
    }
  }
  return reeksen;
}

function jjjjmmdd(datum: Date): string {
  return datum.toISOString().slice(0, 10);
}

/** Op vijf afgerond, zoals je het in Realworks zelf zou intypen. */
function opVijf(waarde: number): number {
  return Math.round(waarde / 5) * 5;
}

/** De marges op de woonruimte waaruit je kiest; 10% is de standaard. */
export const M2_MARGES = [5, 10, 15, 20, 25] as const;
export const M2_MARGE_STANDAARD = 10;

/** Woonruimte van en t/m bij een marge in procenten, op vijf afgerond (MA-67). */
export function m2Grenzen(m2: number, margePct: number): { m2Van: number; m2Tm: number } {
  return {
    m2Van: opVijf(m2 * (1 - margePct / 100)),
    m2Tm: opVijf(m2 * (1 + margePct / 100)),
  };
}

/**
 * De standaardfilters voor een zoekopdracht: woonruimte binnen 10% van het
 * doelpand, op vijf afgerond (60 m² wordt 55 t/m 65, zoals Feico het met de hand
 * zocht; MA-67), dezelfde soort woning, en twee jaar terug. Het scherm van de
 * taxatie filtert daarna nog eens op 25%, dus wie breder zoekt, ziet het daar.
 * Ouder dan een jaar vraagt een indexcorrectie, maar de verkopen van het tweede
 * jaar zijn nog bruikbaar als ijkpunt.
 */
export function standaardFilters(
  doel: { oppervlakteM2: number | null; woningtype?: string | null },
  nu: Date,
): RealworksFilters {
  const m2 = doel.oppervlakteM2;
  const type = doel.woningtype?.toLowerCase() ?? '';
  const twee = new Date(nu);
  twee.setUTCFullYear(twee.getUTCFullYear() - 2);
  return {
    ...(m2 ? m2Grenzen(m2, M2_MARGE_STANDAARD) : { m2Van: null, m2Tm: null }),
    soort: type.startsWith('appartement')
      ? 'appartement'
      : type === 'huis' || type.includes('woonhuis')
        ? 'woonhuis'
        : 'alles',
    datumVan: jjjjmmdd(twee),
    datumTm: jjjjmmdd(nu),
  };
}

/** De filters in één regel, voor een lijst of een knop. */
export function filtersInWoorden(filters: RealworksFilters): string {
  const delen: string[] = ['verkocht'];
  if (filters.m2Van != null || filters.m2Tm != null) {
    delen.push(`${filters.m2Van ?? '…'}-${filters.m2Tm ?? '…'} m²`);
  }
  if (filters.soort !== 'alles') delen.push(filters.soort);
  if (filters.datumVan || filters.datumTm) {
    const nl = (waarde: string | null) => (waarde ? waarde.split('-').reverse().join('-') : '…');
    delen.push(`${nl(filters.datumVan)} t/m ${nl(filters.datumTm)}`);
  }
  return delen.join(', ');
}
